import { createContext } from "react";
import type { Role } from "@/types/database.types";

export interface PermissionsState {
  permissionKeys: string[];
  role: Role | null;
  isLoading: boolean;
  /**
   * True once permissions have been resolved at least once FOR THE CURRENT
   * USER. Distinct from `!isLoading`, which also dips false during a refresh.
   *
   * AppReadyProvider gates the entire app on this rather than on `isLoading`:
   * blocking on `isLoading` means any refreshPermissions() call unmounts the
   * whole tree mid-render, which silently cancels whatever the unmounted page
   * was doing. It reset on a user change so a new sign-in cannot briefly see
   * the previous user's permissions.
   */
  hasLoadedOnce: boolean;
  error: string | null;
  lastRefreshed: Date | null;
}

export interface PermissionsContextType extends PermissionsState {
  hasPermission: (permissionKey: string) => boolean;
  hasAnyPermission: (permissionKeys: string[]) => boolean;
  hasAllPermissions: (permissionKeys: string[]) => boolean;
  isSuperAdmin: () => boolean;
  isAdmin: () => boolean;
  canManage: (
    entity: "users" | "roles" | "permissions" | "services" | "applications",
  ) => boolean;
  refreshPermissions: () => Promise<void>;
}

export const PermissionsContext = createContext<PermissionsContextType | null>(
  null,
);

export const defaultPermissionsContextValue: PermissionsContextType = {
  permissionKeys: [],
  role: null,
  isLoading: true,
  hasLoadedOnce: false,
  error: null,
  lastRefreshed: null,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  isSuperAdmin: () => false,
  isAdmin: () => false,
  canManage: () => false,
  refreshPermissions: async () => {},
};
